<script lang="ts">
  import { onMount } from 'svelte';
  import { Check, Copy, ExternalLink, Loader2 } from 'lucide-svelte';

  export let accessToken: string;

  let loading = true;
  let error = '';
  let order: any = null;
  let copiedKey: string | null = null;

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  onMount(async () => {
    await fetchOrder();
  });

  async function fetchOrder() {
    loading = true;
    error = '';

    try {
      const response = await fetch(`${apiUrl}/api/orders/${accessToken}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load order');
      }

      order = data;
    } catch (err: any) {
      console.error('Error fetching order:', err);
      error = err.message || 'Failed to load order details';
    } finally {
      loading = false;
    }
  }

  async function copyToClipboard(text: string, keyId: string) {
    try {
      await navigator.clipboard.writeText(text);
      copiedKey = keyId;
      setTimeout(() => {
        copiedKey = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'completed':
      case 'paid':
        return 'text-green-400';
      case 'pending':
      case 'confirming':
        return 'text-yellow-400';
      case 'failed':
      case 'expired':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  }

  function getStatusBadge(status: string): string {
    switch (status) {
      case 'completed':
      case 'paid':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'pending':
      case 'confirming':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'failed':
      case 'expired':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    }
  }
</script>

<div class="min-h-screen bg-black text-white">
  <!-- Header -->
  <header class="border-b border-white/10">
    <div class="max-w-5xl mx-auto px-4 py-6">
      <a href="/" class="text-2xl font-bold hover:text-slate-300 transition-colors">
        BuyKeys
      </a>
    </div>
  </header>

  <!-- Main Content -->
  <main class="max-w-5xl mx-auto px-4 py-8 md:py-12">
    {#if loading}
      <!-- Loading State -->
      <div class="flex flex-col items-center justify-center py-20">
        <Loader2 class="w-12 h-12 text-white animate-spin mb-4" />
        <p class="text-slate-400">Loading order details...</p>
      </div>
    {:else if error}
      <!-- Error State -->
      <div class="bg-red-500/10 border border-red-500/20 rounded-xl p-6 md:p-8">
        <h2 class="text-2xl font-bold text-red-400 mb-2">Order Not Found</h2>
        <p class="text-slate-300 mb-6">{error}</p>
        <a
          href="/"
          class="inline-block px-6 py-3 bg-white text-black rounded-lg hover:bg-white/90 transition-colors"
        >
          Back to Home
        </a>
      </div>
    {:else if order}
      <!-- Order Details -->
      <div class="space-y-6">
        <!-- Order Header -->
        <div class="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h1 class="text-3xl md:text-4xl font-bold mb-2">
                Order #{order.order.order_number}
              </h1>
              <p class="text-slate-400">
                {new Date(order.order.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <span
              class="px-4 py-2 rounded-lg border text-sm font-medium {getStatusBadge(
                order.order.payment_status
              )}"
            >
              {order.order.payment_status.toUpperCase()}
            </span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-white/10">
            <div>
              <p class="text-slate-500 text-sm mb-1">Customer Email</p>
              <p class="text-white">{order.order.customer_email}</p>
            </div>
            <div>
              <p class="text-slate-500 text-sm mb-1">Total Amount</p>
              <p class="text-white text-2xl font-semibold">
                ${order.order.total.toFixed(2)} <span class="text-sm font-normal text-slate-400">{order.order.currency}</span>
              </p>
            </div>
            <div>
              <p class="text-slate-500 text-sm mb-1">Order Status</p>
              <p class="{getStatusColor(order.order.status)} font-medium">
                {order.order.status.charAt(0).toUpperCase() + order.order.status.slice(1)}
              </p>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8">
          <h2 class="text-2xl font-bold mb-4">Order Items</h2>
          <div class="space-y-3">
            {#each order.items as item}
              <div class="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p class="text-white font-medium">{item.product_name}</p>
                  <p class="text-slate-400 text-sm">
                    {item.provider.charAt(0).toUpperCase() + item.provider.slice(1)} • Quantity: {item.quantity}
                  </p>
                </div>
                <p class="text-white font-semibold">${item.price.toFixed(2)}</p>
              </div>
            {/each}
          </div>
        </div>

        <!-- API Keys (Only show if payment is complete) -->
        {#if order.order.payment_status === 'paid' && order.api_keys && order.api_keys.length > 0}
          <div class="bg-gradient-to-br from-green-500/10 to-blue-500/10 border border-white/10 rounded-xl p-6 md:p-8">
            <div class="flex items-center gap-2 mb-4">
              <Check class="w-6 h-6 text-green-400" />
              <h2 class="text-2xl font-bold">Your API Keys</h2>
            </div>

            <div class="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
              <p class="text-yellow-400 text-sm">
                ⚠️ Keep these keys secure. Never share them publicly or commit them to version control.
              </p>
            </div>

            <div class="space-y-4">
              {#each order.api_keys as key, idx}
                <div class="bg-black/30 border border-white/10 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                      {key.provider}
                    </span>
                    <button
                      on:click={() => copyToClipboard(key.key_value, `${key.provider}-${idx}`)}
                      class="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-md transition-colors text-sm"
                    >
                      {#if copiedKey === `${key.provider}-${idx}`}
                        <Check class="w-4 h-4 text-green-400" />
                        <span class="text-green-400">Copied!</span>
                      {:else}
                        <Copy class="w-4 h-4" />
                        <span>Copy</span>
                      {/if}
                    </button>
                  </div>
                  <code class="block text-sm text-white/90 font-mono break-all bg-white/5 p-3 rounded">
                    {key.key_value}
                  </code>
                </div>
              {/each}
            </div>
          </div>

          <!-- Usage Instructions -->
          {#if order.instructions}
            <div class="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8">
              <h2 class="text-2xl font-bold mb-4">Getting Started</h2>
              <div class="prose prose-invert max-w-none">
                <pre class="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{order.instructions}</pre>
              </div>
            </div>
          {/if}
        {:else if order.order.payment_status === 'confirming'}
          <!-- Payment Confirming -->
          <div class="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 md:p-8">
            <div class="flex items-center gap-3 mb-4">
              <Loader2 class="w-6 h-6 text-yellow-400 animate-spin" />
              <h2 class="text-2xl font-bold text-yellow-400">Payment Confirming</h2>
            </div>
            <p class="text-slate-300">
              Your payment is being confirmed on the blockchain. This usually takes a few minutes.
              Your API keys will be available here once the payment is confirmed.
            </p>
            <p class="text-slate-400 text-sm mt-4">
              You'll also receive an email at <strong>{order.order.customer_email}</strong> when your keys are ready.
            </p>
          </div>
        {:else if order.order.payment_status === 'pending'}
          <!-- Payment Pending -->
          <div class="bg-slate-500/10 border border-slate-500/20 rounded-xl p-6 md:p-8">
            <h2 class="text-2xl font-bold mb-4">Payment Pending</h2>
            <p class="text-slate-300 mb-4">
              Your order has been created but payment has not been completed yet.
            </p>
            <a
              href="/"
              class="inline-block px-6 py-3 bg-white text-black rounded-lg hover:bg-white/90 transition-colors"
            >
              Return to Home
            </a>
          </div>
        {/if}

        <!-- Payment Transaction Details -->
        {#if order.payment}
          <div class="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8">
            <h2 class="text-2xl font-bold mb-4">Payment Details</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {#if order.payment.payment_method}
                <div>
                  <p class="text-slate-500 text-sm mb-1">Payment Method</p>
                  <p class="text-white font-medium">{order.payment.payment_method}</p>
                </div>
              {/if}
              {#if order.payment.network}
                <div>
                  <p class="text-slate-500 text-sm mb-1">Network</p>
                  <p class="text-white">{order.payment.network}</p>
                </div>
              {/if}
              {#if order.payment.tx_hash}
                <div class="md:col-span-2">
                  <p class="text-slate-500 text-sm mb-1">Transaction Hash</p>
                  <div class="flex items-center gap-2">
                    <code class="text-white text-sm font-mono break-all">{order.payment.tx_hash}</code>
                    <button
                      on:click={() => copyToClipboard(order.payment.tx_hash, 'tx_hash')}
                      class="flex-shrink-0 p-2 hover:bg-white/10 rounded transition-colors"
                      title="Copy transaction hash"
                    >
                      {#if copiedKey === 'tx_hash'}
                        <Check class="w-4 h-4 text-green-400" />
                      {:else}
                        <Copy class="w-4 h-4" />
                      {/if}
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Actions -->
        <div>
          <a
            href="/"
            class="block text-center px-6 py-3 bg-white text-black rounded-lg hover:bg-white/90 transition-colors"
          >
            Back to Home
          </a>
        </div>
      </div>
    {/if}
  </main>

  <!-- Footer -->
  <footer class="border-t border-white/10 mt-20">
    <div class="max-w-5xl mx-auto px-4 py-8 text-center text-slate-400 text-sm">
      <p>© 2024 BuyKeys. All rights reserved.</p>
      <p class="mt-2">
        Need help? Contact us at <a href="mailto:[email protected]" class="text-white hover:underline">[email protected]</a>
      </p>
    </div>
  </footer>
</div>
